
import { useCallback } from 'react';
import { EmailStatistics } from '../types';
import { fetchEmailCounts } from '@/services/api/email';

interface UseEmailDatabaseOperationsProps {
  setStatistics: (stats: EmailStatistics) => void;
  setError: (error: boolean) => void;
}

// Cache for statistics to avoid redundant API calls
let statisticsCache: EmailStatistics | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 30000; // 30 seconds

export const useEmailDatabaseOperations = ({
  setStatistics,
  setError,
}: UseEmailDatabaseOperationsProps) => {

  const initializeDatabase = useCallback(async () => {
    try {
      console.log('📧 Initializing JWT-based email system...');
      
      // Check cache first
      const now = Date.now();
      if (statisticsCache && (now - cacheTimestamp) < CACHE_DURATION) {
        console.log('📧 Using cached statistics');
        setStatistics(statisticsCache);
        return true;
      }
      
      // Backend handles user scoping via JWT automatically
      // Just fetch statistics to verify connection
      const stats = await loadStatisticsFromAPI();
      setStatistics(stats);
      
      console.log('✅ Email system initialized');
      return true;
    } catch (error: any) {
      console.error('❌ Failed to initialize email system:', error);
      
      // Check if it's an authentication error (401)
      const isAuthError = error?.status === 401 || 
                         error?.message?.includes('401') ||
                         error?.message?.includes('Invalid token') ||
                         error?.message?.includes('Unauthorized');
      
      if (!isAuthError) {
        // Only set error state for non-auth errors
        setError(true);
      } else {
        console.error('🔒 Authentication error - email endpoints rejecting token');
      }
      return false;
    }
  }, [setStatistics, setError]);

  const loadStatisticsFromAPI = async (): Promise<EmailStatistics> => {
    try {
      console.log('📊 Loading email statistics from /emails/counts...');
      const response = await fetchEmailCounts();
      
      // Transform new structure to frontend format
      const folderCounts: Record<string, number> = {};
      let inboxUnread = 0;
      
      if (Array.isArray(response)) {
        response.forEach((item) => {
          folderCounts[item.name] = item.total_count || 0;
          if (item.name === 'inbox') {
            inboxUnread = item.unread_count || 0;
          }
        });
      }
      
      const stats: EmailStatistics = {
        unread: inboxUnread,
        total: {
          inbox: folderCounts.inbox || 0,
          sent: folderCounts.sent || 0,
          drafts: folderCounts.drafts || 0,
          trash: folderCounts.trash || 0,
        }
      };
      
      // Cache the results
      statisticsCache = stats;
      cacheTimestamp = Date.now();
      
      console.log('✅ Statistics loaded:', stats);
      return stats;
    } catch (error) {
      console.error('💥 Failed to load statistics:', error);
      return {
        unread: 0,
        total: { inbox: 0, sent: 0, drafts: 0, trash: 0 }
      };
    }
  };

  const loadStatistics = useCallback(async (force = false) => {
    try {
      console.log('📊 Loading email statistics...');
      
      // Use cached data if available and fresh
      const now = Date.now();
      if (!force && statisticsCache && (now - cacheTimestamp) < CACHE_DURATION) {
        console.log('📊 Using cached statistics for loadStatistics');
        setStatistics(statisticsCache);
        return statisticsCache;
      }
      
      // Fetch from API
      const stats = await loadStatisticsFromAPI();
      setStatistics(stats);
      return stats;
    } catch (error) {
      console.error('💥 Failed to load statistics:', error);
      return {
        unread: 0,
        total: { inbox: 0, sent: 0, drafts: 0, trash: 0 }
      };
    }
  }, [setStatistics]);

  return {
    initializeDatabase,
    loadStatistics,
  };
};
